import React, { useState, memo, useCallback, useMemo } from "react";
import { NavLink, useLoaderData, useNavigate, useSubmit } from "@remix-run/react";
import { LogOut } from "lucide-react";
import { ScrollArea } from "~/components/general/scroll-area";
import { Button } from "~/components/general/Button";
import logo from "~/assets/logo.svg";
import { NavigationItems } from "~/NavigationItems";
import { toast } from "sonner";
import { Role } from "~/utils/schema/schema";
import { useTranslation } from "react-i18next";
import { LoaderData } from "~/root";

const SidebarContent = memo(({ partnerId }: { partnerId: string }) => {
  const submit = useSubmit();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { role } = useLoaderData<LoaderData>();

  const handleLogout = useCallback(() => {
    toast.success("Logout Successful", {
      dismissible: true,
      duration: 1500,
    });
    submit(null, { action: "/logout", method: "post" });
  }, [submit]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleNavigate = useCallback(() => navigate("/"), [navigate]);

  const navigationItems = useMemo(() => 
    NavigationItems(i18n, partnerId), [i18n, partnerId]);

  return (
    <ScrollArea
      className="h-full py-6"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`px-3 pb-2 transition-all duration-500 ease-in-out ${
          isHovered ? "w-[240px]" : "w-[70px]"
        }`}
      >
        <div
          onClick={handleNavigate}
          className={
            "cursor-pointer md:hidden flex flex-row items-center justify-center mb-10 "
          }
        >
          <img src={logo} alt="SGB Logo" className="h-20 w-18" />
        </div>
        <div className="space-y-1">
          {navigationItems.map((item) => {
            if (item.role) {
              return (
                role === Role.SGB && (
                  <NavLink key={item.to} to={item.to} className="w-full">
                    {({ isActive }) => (
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        className={`w-full justify-start transition-all duration-500 ease-in-out ${
                          isHovered ? "px-4" : "px-2"
                        }`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        <span
                          className={`ml-2 transition-opacity duration-500 ${
                            isHovered
                              ? "opacity-100"
                              : "opacity-0 w-0 overflow-hidden"
                          }`}
                        >
                          {item.label}
                        </span>
                      </Button>
                    )}
                  </NavLink>
                )
              );
            }
            return (
              <NavLink key={item.to} to={item.to} className="w-full">
                {({ isActive }) => (
                  <Button
                    variant={isActive ? "secondary" : "ghost"}
                    className={`w-full justify-start transition-all duration-500 ease-in-out ${
                      isHovered ? "px-4" : "px-2"
                    }`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span
                      className={`ml-2 transition-opacity duration-500 ${
                        isHovered
                          ? "opacity-100"
                          : "opacity-0 w-0 overflow-hidden"
                      }`}
                    >
                      {item.label}
                    </span>
                  </Button>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>
      <div className="px-3 py-2">
        <Button
          onClick={handleLogout}
          variant="ghost"
          className={`w-full justify-start transition-all duration-500 ease-in-out ${
            isHovered ? "px-4" : "px-2"
          }`}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          <span
            className={`ml-2 transition-opacity duration-500 ${
              isHovered ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            }`}
          >
            {i18n.t("navigation.logout")}
          </span>
        </Button>
      </div>
    </ScrollArea>
  );
});

const Sidebar = memo(({ partnerId }: { partnerId: string }) => {
  return (
    <>
      <div className="hidden min-w-[80px] border-r bg-gray-100/40 md:block dark:bg-gray-800/40 transition-all duration-500 ease-in-out hover:min-w-[200px]">
        <SidebarContent partnerId={partnerId} />
      </div>
    </>
  );
});

export default Sidebar;
